import { create } from 'zustand'
import { CompliancePack, ComplianceStub } from '../types'

interface CompliancePackState {
  pack: CompliancePack | null
  focusedStubId: string | null
  isLoading: boolean
  error: string | null

  /**
   * Replace (or clear) the in-memory compliance pack.
   *
   * The triage list now passes ``null`` whenever the project / run
   * changes and no pack-on-disk was found, so the architect doesn't
   * see a previous project's already-confirmed stubs while the new
   * project's Phase 2.5 is still running.
   */
  setPack: (pack: CompliancePack | null) => void
  setFocusedStub: (stubId: string) => void
  /**
   * Update a stub's status (and optionally its EARS text + justification).
   *
   * Plan §1.2 acceptance criterion #2: when ``status === 'dropped'``, the
   * caller MUST supply a ``justification`` so the audit trail records why
   * the architect declined a regulatory-required control. The store
   * stores the justification on ``inheritance_notes`` of the stub itself
   * (which the SRD merger and exporter both read), and also moves the
   * stub_id into the pack's ``dropped[]`` ledger so the Phase 5 export
   * can render a "Compliance deferral log" appendix without re-walking
   * every stub.
   */
  updateStubStatus: (
    stubId: string,
    status: ComplianceStub['status'],
    ears_text?: string,
    justification?: string,
  ) => void
}

export const useCompliancePackStore = create<CompliancePackState>((set) => ({
  pack: null,
  focusedStubId: null,
  isLoading: false,
  error: null,

  setPack: (pack) => set({ pack }),

  setFocusedStub: (stubId) => set({ focusedStubId: stubId }),

  updateStubStatus: (stubId, status, ears_text, justification) =>
    set((state) => {
      if (!state.pack) return state

      const updatedStubs = state.pack.stubs.map((stub) => {
        if (stub.stub_id !== stubId) return stub
        const next: ComplianceStub = {
          ...stub,
          status,
          ...(ears_text !== undefined ? { ears_text } : {}),
        }
        if (status === 'dropped' && justification) {
          next.inheritance_notes = justification
        }
        return next
      })

      // Maintain the pack-level ``dropped[]`` ledger so the exporter has
      // a single place to read the audit trail. We re-derive it from
      // the stubs every update to keep it consistent with the source of
      // truth.
      const dropped = updatedStubs
        .filter((s) => s.status === 'dropped')
        .map((s) => ({
          stub_id: s.stub_id,
          reason: s.inheritance_notes ?? '',
          by: 'user',
          at: new Date().toISOString(),
        }))

      return {
        pack: {
          ...state.pack,
          stubs: updatedStubs,
          dropped,
        },
      }
    }),
}))
